// @ts-nocheck
/**
 * Databook agent page: the Full Statements flow, pointed at one client's
 * databook template. One parent session takes MANY workbooks at once; a
 * server-side consolidation step works out which sheets feed the income
 * statement and which the balance sheet, then the two existing agents map
 * them back-to-back as child sessions (see UnifiedAgent.tsx) into the
 * CLIENT'S category list, which the parent snapshot serves as `taxonomy`.
 * The result is the Full Statements mega-card with one export: the client's
 * own workbook.
 *
 * Flow: one multi-file dropzone (no sheet picking, no slot assignment; files
 * upload one after another), one Run click. From there the page is
 * UnifiedAgent: it watches the two child streams in sequence, and
 * restore/refresh loads the parent snapshot and reattaches to whichever
 * child is still active. Unlike the unified snapshot, the databook snapshot
 * also answers before the first run, so a refresh during setup restores the
 * uploaded list. Structure mirrors UnifiedAgent.tsx deliberately.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  BookOpen,
  FileSpreadsheet,
  X,
  RotateCcw,
  Sparkles,
  Upload,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  createAgentSession,
  uploadDatabookFile,
  startDatabookRun,
  getDatabookSession,
  watchAgentSession,
} from '../../utils/agentApi';
import ToolCallBlock from './ToolCallBlock';
import FinalMappingCardUnified from './FinalMappingCardUnified';
import DatabookExportButton from './DatabookExportButton';
import { TaxonomyProvider } from './taxonomyContext';
import './Agent.css';
import './UnifiedAgent.css';
import './DatabookAgent.css';

const newId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const ROLE_LABELS = {
  pnl: 'Income statement',
  bs: 'Balance sheet',
};

const PHASE_ORDER = ['pnl', 'bs'];

// The pre-agent step that combines the uploaded workbooks. It streams as an
// ordinary tool card on the P&L child.
const CONSOLIDATE_TOOL = 'consolidate_workbooks';

const WORKBOOK_TYPES = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
  'application/vnd.ms-excel': ['.xls'],
};

const fileSignature = (f) => `${f.name}:${f.size}`;

// Which source sheets fed each statement, as [{ filename, sheet, ... }] per
// role. The durable record is `ingest_used` on each child's first file_ref;
// during a live run the consolidation result carries the same facts as
// `statements[role].sources`, before any snapshot has been read.
const perRole = (pick) => {
  const out = {};
  for (const role of PHASE_ORDER) {
    const used = pick(role);
    out[role] = Array.isArray(used) && used.length > 0 ? used : null;
  }
  return out;
};
const sourcesFromSnapshot = (snap) =>
  perRole((role) => snap?.children?.[role]?.file_refs?.[0]?.ingest_used);
const sourcesFromConsolidation = (result) =>
  perRole((role) => result?.statements?.[role]?.sources);

const DatabookAgent = ({ initialSessionId, onSessionIdChange, onSessionsRefresh }) => {
  const [sessionId, setSessionId] = useState(initialSessionId || null);
  // Workbooks dropped so far, in drop order:
  //   { key, name, size, status, uploaded, error }
  // status is queued | uploading | done | error; `uploaded` is the upload
  // response ({ file_id, filename, size_bytes, sheets }).
  const [files, setFiles] = useState([]);
  const [chatItems, setChatItems] = useState([]);
  // Captured mapping payloads per statement, fed to the mega-card.
  const [mappings, setMappings] = useState({ pnl: null, bs: null });
  const [childIds, setChildIds] = useState({ pnl: null, bs: null });
  // The client's category list, from the parent snapshot.
  const [taxonomy, setTaxonomy] = useState(null);
  const [sources, setSources] = useState({ pnl: null, bs: null });
  const [phase, setPhase] = useState(initialSessionId ? 'loading' : 'idle'); // idle | running | done | error | loading
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const restoredSessionRef = useRef(null);
  const onSessionIdChangeRef = useRef(onSessionIdChange);
  const onSessionsRefreshRef = useRef(onSessionsRefresh);
  useEffect(() => {
    onSessionIdChangeRef.current = onSessionIdChange;
    onSessionsRefreshRef.current = onSessionsRefresh;
  }, [onSessionIdChange, onSessionsRefresh]);
  const runActivityNotifiedRef = useRef(false);

  // ---- upload queue -----------------------------------------------------
  // Parallel uploads to one session race server-side, so dropped files go
  // through a single worker that awaits each upload before starting the
  // next. The worker outlives renders, so the list and the session id it
  // works on live in refs (mirrored into state for rendering).
  const filesRef = useRef([]);
  const sessionIdRef = useRef(initialSessionId || null);
  const queueRef = useRef([]); // { key, file } still to upload
  const uploadGenRef = useRef(0); // bumped to cancel everything queued or in flight
  const drainingGenRef = useRef(null); // the generation a worker is draining
  const uploadAbortRef = useRef(null);

  const commitFiles = useCallback((next) => {
    filesRef.current = next;
    setFiles(next);
  }, []);

  const cancelUploads = useCallback(() => {
    uploadGenRef.current += 1;
    queueRef.current = [];
    uploadAbortRef.current?.abort();
  }, []);

  const drainUploads = useCallback(async () => {
    const gen = uploadGenRef.current;
    if (drainingGenRef.current === gen) return;
    drainingGenRef.current = gen;
    const live = () => uploadGenRef.current === gen;
    try {
      while (live() && queueRef.current.length > 0) {
        const { key, file } = queueRef.current.shift();
        const patch = (fields) =>
          commitFiles(
            filesRef.current.map((f) => (f.key === key ? { ...f, ...fields } : f)),
          );
        patch({ status: 'uploading' });
        const ac = new AbortController();
        uploadAbortRef.current = ac;
        try {
          let sid = sessionIdRef.current;
          if (!sid) {
            ({ session_id: sid } = await createAgentSession());
            if (!live()) return;
            sessionIdRef.current = sid;
            setSessionId(sid);
          }
          const uploaded = await uploadDatabookFile(sid, file, ac.signal);
          if (!live()) return;
          patch({ status: 'done', uploaded, error: null });
          // Publish the session (URL + sidebar) only once it holds a file:
          // from here a refresh restores the list from the snapshot.
          if (restoredSessionRef.current !== sid) {
            restoredSessionRef.current = sid;
            onSessionIdChangeRef.current?.(sid);
          }
        } catch (err) {
          if (!live()) return;
          patch({ status: 'error', error: err.message || String(err) });
        }
      }
    } finally {
      if (drainingGenRef.current === gen) drainingGenRef.current = null;
    }
  }, [commitFiles]);

  const addFiles = useCallback(
    (accepted, rejected = []) => {
      setError(
        rejected.length > 0
          ? `${rejected.length === 1 ? '1 file was' : `${rejected.length} files were`} skipped. Only .xlsx, .xlsm and .xls workbooks can be added.`
          : null,
      );
      // A workbook already in the list (same name and size) is not uploaded
      // twice. Dropping one that failed replaces its row, which retries it.
      const listed = new Set(
        filesRef.current.filter((f) => f.status !== 'error').map(fileSignature),
      );
      const fresh = [];
      for (const file of accepted) {
        const signature = fileSignature(file);
        if (listed.has(signature)) continue;
        listed.add(signature);
        fresh.push({ key: newId(), file });
      }
      if (fresh.length === 0) return;
      const retried = new Set(fresh.map(({ file }) => fileSignature(file)));
      queueRef.current.push(...fresh);
      commitFiles([
        ...filesRef.current.filter((f) => !retried.has(fileSignature(f))),
        ...fresh.map(({ key, file }) => ({
          key,
          name: file.name,
          size: file.size,
          status: 'queued',
        })),
      ]);
      drainUploads();
    },
    [commitFiles, drainUploads],
  );

  // Removal is client-side: the workbook stays on the session, its id is
  // just not sent with the run.
  const removeFile = useCallback(
    (key) => {
      queueRef.current = queueRef.current.filter((q) => q.key !== key);
      commitFiles(filesRef.current.filter((f) => f.key !== key));
    },
    [commitFiles],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    cancelUploads();
    sessionIdRef.current = null;
    setSessionId(null);
    commitFiles([]);
    setChatItems([]);
    setMappings({ pnl: null, bs: null });
    setChildIds({ pnl: null, bs: null });
    setTaxonomy(null);
    setSources({ pnl: null, bs: null });
    setPhase('idle');
    setError(null);
    restoredSessionRef.current = null;
    onSessionIdChangeRef.current?.(null);
  }, [cancelUploads, commitFiles]);

  const handleMappingUpdated = useCallback((role, updatedMapping) => {
    setMappings((prev) => ({ ...prev, [role]: updatedMapping }));
  }, []);

  const mergeSources = useCallback((next) => {
    setSources((prev) => ({
      pnl: next.pnl || prev.pnl,
      bs: next.bs || prev.bs,
    }));
  }, []);

  // ---- run + watch -----------------------------------------------------

  const watchPhases = useCallback(
    async (phases, signal, resumeOffsets = {}) => {
      for (const ph of phases) {
        const role = ph.statement_type;
        setChildIds((prev) => ({ ...prev, [role]: ph.session_id }));
        setChatItems((prev) => {
          // One marker per phase; skip if the restore already added it.
          if (prev.some((it) => it.type === 'phase_marker' && it.role === role)) {
            return prev;
          }
          return [
            ...prev,
            { id: newId(), type: 'phase_marker', role },
          ];
        });
        await watchAgentSession(
          ph.session_id,
          resumeOffsets[role] || '0',
          (event, _eventId) => {
            if (!runActivityNotifiedRef.current) {
              runActivityNotifiedRef.current = true;
              onSessionsRefreshRef.current?.();
            }
            handleDatabookEvent(event, role, setChatItems, setMappings, mergeSources);
          },
          signal,
        );
        if (signal.aborted) return;
      }
    },
    [mergeSources],
  );

  // Reconcile with the server after the streams close: pick up final child
  // statuses + any mapping the stream missed (fallbacks live server-side),
  // and the durable record of which sheets fed which statement.
  const finalizeFromSnapshot = useCallback(
    async (sid) => {
      try {
        const snap = await getDatabookSession(sid);
        const nextMappings = {};
        const nextChildIds = {};
        for (const role of PHASE_ORDER) {
          const child = snap.children?.[role];
          nextChildIds[role] = snap.pair?.[role]?.child_session_id || null;
          if (!child) continue;
          const items = child.view_state?.chat_items || [];
          const found = items.find((it) => it?.type === 'mapping_result');
          nextMappings[role] = found?.mapping || child.final_mapping || null;
        }
        setChildIds((prev) => ({ ...prev, ...nextChildIds }));
        setMappings((prev) => ({
          pnl: nextMappings.pnl || prev.pnl,
          bs: nextMappings.bs || prev.bs,
        }));
        if (snap.taxonomy) setTaxonomy(snap.taxonomy);
        mergeSources(sourcesFromSnapshot(snap));
        return snap;
      } catch {
        return null;
      }
    },
    [mergeSources],
  );

  const uploading = files.some(
    (f) => f.status === 'queued' || f.status === 'uploading',
  );
  const canRun = !uploading && files.some((f) => f.status === 'done');

  const runDatabook = useCallback(async () => {
    if (!canRun || !sessionId) return;
    const fileIds = files
      .filter((f) => f.status === 'done')
      .map((f) => f.uploaded.file_id);
    setError(null);
    setPhase('running');
    setChatItems([]);
    setMappings({ pnl: null, bs: null });
    setSources({ pnl: null, bs: null });
    runActivityNotifiedRef.current = false;
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      let result;
      try {
        // A 409 (chain already live in another tab) resolves with the same
        // phases: both tabs converge on the same child streams.
        result = await startDatabookRun(sessionId, fileIds);
      } catch (err) {
        if (ac.signal.aborted) return;
        // Nothing started. Back to setup, so the uploaded list survives and
        // the run can be retried.
        setError(err.message || String(err));
        setPhase('idle');
        return;
      }
      if (ac.signal.aborted) return;
      // The category list rides on the snapshot, not on the run response.
      // Read it now so the live classify cards and the result card use it;
      // the post-run snapshot read sets it again if this one fails.
      getDatabookSession(sessionId)
        .then((snap) => {
          if (!ac.signal.aborted && snap?.taxonomy) setTaxonomy(snap.taxonomy);
        })
        .catch(() => {});
      await watchPhases(result.phases || [], ac.signal);
      if (ac.signal.aborted) return;
      const snap = await finalizeFromSnapshot(sessionId);
      if (ac.signal.aborted) return;
      if (snap?.status === 'failed') {
        setError('The run did not complete for both statements, so the databook cannot be exported yet.');
      }
      setPhase('done');
    } catch (err) {
      if (ac.signal.aborted || err.name === 'AbortError') return;
      setError(err.message || String(err));
      setPhase('error');
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
      if (!ac.signal.aborted) {
        setChatItems((prev) =>
          prev.map((it) =>
            it.type === 'assistant' && it.isStreaming
              ? { ...it, isStreaming: false }
              : it.type === 'tool_group' && it.isRunning
                ? { ...it, isRunning: false }
                : it,
          ),
        );
      }
    }
  }, [canRun, sessionId, files, watchPhases, finalizeFromSnapshot]);

  // ---- restore ----------------------------------------------------------

  useEffect(() => {
    if (!initialSessionId) {
      // Also covers a load that never finished or failed (no session was
      // restored, but the page is not on a fresh setup either).
      if (restoredSessionRef.current || phase !== 'idle') reset();
      return;
    }
    if (restoredSessionRef.current === initialSessionId) return;

    abortRef.current?.abort();
    cancelUploads();
    const ac = new AbortController();
    abortRef.current = ac;

    setError(null);
    setPhase('loading');
    commitFiles([]);
    setChatItems([]);
    setMappings({ pnl: null, bs: null });
    setTaxonomy(null);
    setSources({ pnl: null, bs: null });

    (async () => {
      try {
        const snap = await getDatabookSession(initialSessionId);
        if (ac.signal.aborted) return;
        restoredSessionRef.current = initialSessionId;
        sessionIdRef.current = initialSessionId;
        setSessionId(initialSessionId);
        setTaxonomy(snap.taxonomy || null);
        commitFiles(
          (snap.inputs || []).map((input) => ({
            key: input.file_id,
            name: input.filename,
            size: input.size_bytes,
            status: 'done',
            uploaded: input,
          })),
        );

        // Not run yet: nothing to replay. Back to setup with the uploaded
        // list restored.
        if (!snap.pair) {
          setPhase('idle');
          return;
        }

        // Merge the children's persisted timelines with phase markers;
        // mapping_result items are captured, not rendered inline.
        const items = [];
        const restoredMappings = { pnl: null, bs: null };
        const restoredChildIds = { pnl: null, bs: null };
        const activePhases = [];
        const resumeOffsets = {};
        for (const role of PHASE_ORDER) {
          const child = snap.children?.[role];
          restoredChildIds[role] =
            snap.pair?.[role]?.child_session_id || null;
          if (!child) continue;
          const childItems = child.view_state?.chat_items || [];
          // Only a phase that has started gets its divider here. Both
          // children exist (and read as active) from the moment the run
          // starts, so a divider for the waiting one would land ahead of
          // the first phase's remaining events; the watcher adds it when
          // that phase's stream opens.
          if (childItems.length > 0) {
            items.push({ id: newId(), type: 'phase_marker', role });
          }
          for (const it of childItems) {
            if (it?.type === 'mapping_result') {
              restoredMappings[role] = it.mapping;
              items.push({ id: newId(), type: 'phase_done', role });
            } else {
              items.push({ ...it, id: it.id || newId() });
            }
          }
          if (!restoredMappings[role] && child.final_mapping) {
            restoredMappings[role] = child.final_mapping;
          }
          if (child.status === 'active') {
            activePhases.push({
              statement_type: role,
              session_id: child.session_id,
            });
            resumeOffsets[role] =
              child.view_state?._last_event_id || child.last_event_id || '0';
          }
        }
        setChatItems(items);
        setMappings(restoredMappings);
        setChildIds(restoredChildIds);
        setSources(sourcesFromSnapshot(snap));

        if (snap.status === 'active') {
          setPhase('running');
          // Also queue the not-yet-started phase so the watcher rolls
          // straight into it when the chain gets there.
          const phasesToWatch = [...activePhases];
          for (const role of PHASE_ORDER) {
            const child = snap.children?.[role];
            const already = phasesToWatch.some(
              (p) => p.statement_type === role,
            );
            const done = child && child.status !== 'active';
            const cid = snap.pair?.[role]?.child_session_id;
            if (!already && !done && cid) {
              phasesToWatch.push({ statement_type: role, session_id: cid });
            }
          }
          try {
            await watchPhases(phasesToWatch, ac.signal, resumeOffsets);
            if (!ac.signal.aborted) {
              await finalizeFromSnapshot(initialSessionId);
              if (!ac.signal.aborted) setPhase('done');
            }
          } catch (err) {
            if (ac.signal.aborted || err.name === 'AbortError') return;
            setError(err.message || String(err));
            setPhase('error');
          }
        } else {
          setPhase('done');
        }
      } catch (err) {
        if (ac.signal.aborted) return;
        setError(err?.message || String(err));
        setPhase('error');
      }
    })();

    return () => {
      ac.abort();
    };
    // Pinned to initialSessionId only. See Agent.tsx for the rationale
    // (parent re-renders every 5s from the sidebar poll; identity churn on
    // the callbacks would abort in-flight restores).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      cancelUploads();
    },
    [cancelUploads],
  );

  const showSetup = phase === 'idle';

  return (
    <div className="agent-root">
      <div className="agent-header">
        <div className="agent-header-inner">
          <div className="agent-header-title">
            <BookOpen size={18} />
            <span>Databook</span>
          </div>
          <div className="agent-header-actions">
            {!showSetup && (
              <div className="agent-file-chips">
                {PHASE_ORDER.map((role) => {
                  const used = sources[role];
                  if (!used) return null;
                  const fileCount = new Set(
                    used.map((u) => u.file_id || u.filename),
                  ).size;
                  return (
                    <span
                      key={role}
                      className="agent-file-chip"
                      title={[
                        ROLE_LABELS[role],
                        ...used.map((u) =>
                          u.sheet ? `${u.filename} · ${u.sheet}` : u.filename,
                        ),
                      ].join('\n')}
                    >
                      <FileSpreadsheet size={12} />
                      <span className="agent-file-chip-name">
                        {ROLE_LABELS[role]}
                        <span className="agent-file-chip-divider"> · </span>
                        {fileCount} {fileCount === 1 ? 'file' : 'files'}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
            {!showSetup && (
              <button
                className="agent-newchat-btn"
                onClick={reset}
                disabled={phase === 'running'}
                title="Build another databook"
              >
                <RotateCcw size={12} style={{ marginRight: 4 }} />
                Build another
              </button>
            )}
          </div>
        </div>
      </div>

      {showSetup ? (
        <SetupView
          files={files}
          uploading={uploading}
          onAdd={addFiles}
          onRemove={removeFile}
          canRun={canRun}
          onRun={runDatabook}
        />
      ) : (
        // Wraps the timeline as well as the result card: the classify tool
        // cards badge categories from the same list.
        <TaxonomyProvider taxonomy={taxonomy}>
          <DatabookRunView
            phase={phase}
            chatItems={chatItems}
            mappings={mappings}
            childIds={childIds}
            parentId={sessionId}
            onMappingUpdated={handleMappingUpdated}
          />
        </TaxonomyProvider>
      )}

      {error && (
        <div className="agent-error-banner">
          <X size={14} />
          {error}
          <button onClick={() => setError(null)} className="agent-error-dismiss">
            dismiss
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Setup view: one multi-file dropzone, the workbook list, Run
// ---------------------------------------------------------------------------

const FileRow = ({ entry, onRemove }) => {
  const sheetCount = entry.uploaded?.sheets?.length ?? 0;
  const statusText =
    entry.status === 'done'
      ? `${sheetCount} ${sheetCount === 1 ? 'sheet' : 'sheets'}`
      : entry.status === 'error'
        ? entry.error
        : entry.status === 'uploading'
          ? 'Uploading…'
          : 'Waiting';
  return (
    <li className={`db-file is-${entry.status}`}>
      <FileSpreadsheet size={14} />
      <span className="ua-slot-filename" title={entry.name}>
        {entry.name}
      </span>
      <span className="db-file-status" title={statusText}>
        {entry.status === 'uploading' && <Loader2 size={12} className="spin" />}
        {entry.status === 'done' && <CheckCircle size={12} />}
        {entry.status === 'error' && <AlertTriangle size={12} />}
        <span className="db-file-status-text">{statusText}</span>
      </span>
      <button
        type="button"
        className="ua-slot-clear"
        onClick={() => onRemove(entry.key)}
        disabled={entry.status === 'uploading'}
        title="Remove"
      >
        <X size={12} />
      </button>
    </li>
  );
};

const SetupView = ({ files, uploading, onAdd, onRemove, canRun, onRun }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onAdd,
    accept: WORKBOOK_TYPES,
    multiple: true,
  });

  return (
    <div className="agent-empty ua-setup db-setup">
      <div className="ua-setup-inner">
        <h2 className="agent-empty-title">Build a databook</h2>
        <div
          {...getRootProps()}
          className={`ua-slot-drop db-drop ${isDragActive ? 'drag-active' : ''}`}
        >
          <input {...getInputProps()} />
          <Upload size={18} />
          <span>Drop the management account workbooks here</span>
        </div>
        {files.length > 0 && (
          <ul className="db-files">
            {files.map((entry) => (
              <FileRow key={entry.key} entry={entry} onRemove={onRemove} />
            ))}
          </ul>
        )}
        <button
          type="button"
          className="ua-run-btn"
          onClick={onRun}
          disabled={!canRun}
        >
          <Sparkles size={14} />
          {uploading ? 'Uploading…' : 'Build databook'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Run view: merged two-phase timeline + the mega-card
// ---------------------------------------------------------------------------

const THINKING_PHRASES = {
  consolidate_workbooks: 'Lining up the statements…',
  get_sheet_structure: 'Studying the statement…',
  find_net_income: 'Planning the section breakdown…',
  find_balance_anchors: 'Finding the balance anchors…',
  classify_section: 'Sorting the next section…',
  classify_bs: 'Classifying the balance sheet…',
  read_rows: 'Reading the detail…',
  detect_totals: 'Checking the subtotals…',
  verify_net_profit: 'Reconciling the numbers…',
  check_balance: 'Checking the balance…',
  run_python: 'Crunching the numbers…',
};

const thinkingPhrase = (chatItems) => {
  for (let i = chatItems.length - 1; i >= 0; i--) {
    const it = chatItems[i];
    if (it?.type === 'tool_group' && it.calls?.length) {
      const lastCall = it.calls[it.calls.length - 1];
      return THINKING_PHRASES[lastCall?.toolName] || 'Working through the numbers…';
    }
  }
  return 'Getting oriented…';
};

const ThinkingIndicator = ({ label }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - start) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, []);
  return (
    <motion.div
      className="agent-thinking is-breathing"
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -4 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
    >
      <span className="agent-thinking-orb" aria-hidden="true">
        <span className="agent-thinking-orb-ripple" />
        <span className="agent-thinking-orb-core" />
      </span>
      <span className="agent-thinking-name">Thinking</span>
      <span className="agent-thinking-text">· {label}</span>
      {elapsed >= 1 && <span className="agent-thinking-elapsed">{elapsed}s</span>}
    </motion.div>
  );
};

const summarizeThought = (text, max = 90) => {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const m = cleaned.match(/^[^.!?]+[.!?](?=\s|$)/);
  const first = m ? m[0] : cleaned;
  if (first.length <= max) {
    return first + (cleaned.length > first.length ? '…' : '');
  }
  const cut = first.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut) + '…';
};

const ThoughtTab = ({ text, active, phrase }) => {
  const cleaned = text.replace(/<mapping>[\s\S]*?<\/mapping>/g, '').trim();
  const [expanded, setExpanded] = useState(false);
  if (!cleaned) return null;
  const label = active ? phrase : summarizeThought(cleaned);
  return (
    <div className="agent-thought">
      <button
        type="button"
        className={`agent-thought-header ${active ? 'is-breathing' : ''}`}
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="agent-thinking-orb" aria-hidden="true">
          <span className="agent-thinking-orb-ripple" />
          <span className="agent-thinking-orb-core" />
        </span>
        <span className="agent-thinking-name">Thinking</span>
        <span className="agent-thinking-text">· {label}</span>
        {expanded ? (
          <ChevronDown size={12} className="agent-thought-chevron" />
        ) : (
          <ChevronRight size={12} className="agent-thought-chevron" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="agent-thought-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="agent-thought-prose">{cleaned}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// The consolidation step streams on the P&L child but feeds both statements,
// so its card is shown above the income-statement divider: first in the
// timeline, under neither statement.
const liftConsolidation = (items) => {
  const at = items.findIndex(
    (it) => it.type === 'phase_marker' && it.role === 'pnl',
  );
  const next = at === -1 ? null : items[at + 1];
  if (
    next?.type !== 'tool_group' ||
    next.calls?.[0]?.toolName !== CONSOLIDATE_TOOL
  ) {
    return items;
  }
  const lifted = items.slice();
  lifted[at] = next;
  lifted[at + 1] = items[at];
  return lifted;
};

const DatabookRunView = ({ phase, chatItems, mappings, childIds, parentId, onMappingUpdated }) => {
  const scrollRef = useRef(null);
  const lastItem = chatItems[chatItems.length - 1];
  const lastIndex = chatItems.length - 1;
  const phrase = thinkingPhrase(chatItems);
  const showStandalone =
    phase === 'running' &&
    (chatItems.length === 0 ||
      lastItem?.type === 'phase_marker' ||
      lastItem?.type === 'phase_done' ||
      (lastItem?.type === 'tool_group' && !lastItem.isRunning));

  const bothMapped = !!(mappings.pnl && mappings.bs);
  // A finished run renders whatever survived: a failed child no longer
  // hides the other statement. Mid-run the card still waits for both phases
  // so a still-running second phase isn't presented as failed.
  const showMappingCard =
    bothMapped || (phase === 'done' && !!(mappings.pnl || mappings.bs));

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatItems, showStandalone, showMappingCard]);

  return (
    <div className="agent-run">
      <div className="agent-run-stream" ref={scrollRef}>
        {phase === 'loading' && (
          <div className="agent-run-pending">
            <Sparkles size={14} className="spin-slow" />
            <span>Loading session…</span>
          </div>
        )}

        {liftConsolidation(chatItems).map((item, idx) => {
          if (item.type === 'phase_marker') {
            return (
              <div key={item.id} className="ua-phase-marker">
                <span className="ua-phase-line" aria-hidden />
                <span className="ua-phase-label">
                  {ROLE_LABELS[item.role]} mapping
                </span>
                <span className="ua-phase-line" aria-hidden />
              </div>
            );
          }
          if (item.type === 'phase_done') {
            return (
              <div key={item.id} className="ua-phase-done">
                <CheckCircle size={13} />
                <span>{ROLE_LABELS[item.role]} mapped</span>
              </div>
            );
          }
          if (item.type === 'tool_group') {
            return (
              <ToolCallBlock
                key={item.id}
                calls={item.calls}
                isRunning={item.isRunning}
              />
            );
          }
          if (item.type === 'assistant') {
            const text = (item.text || '').trim();
            if (!text) return null;
            const active = phase === 'running' && idx === lastIndex;
            return (
              <ThoughtTab key={item.id} text={text} active={active} phrase={phrase} />
            );
          }
          return null;
        })}

        <AnimatePresence>
          {showStandalone && (
            <ThinkingIndicator key="thinking-indicator" label={phrase} />
          )}
        </AnimatePresence>

        {showMappingCard && (
          <FinalMappingCardUnified
            parentId={parentId}
            pnl={{ mapping: mappings.pnl, sessionId: childIds.pnl }}
            bs={{ mapping: mappings.bs, sessionId: childIds.bs }}
            onPnlMappingUpdated={(m) => onMappingUpdated('pnl', m)}
            onBsMappingUpdated={(m) => onMappingUpdated('bs', m)}
            runComplete={phase === 'done'}
            ExportControl={DatabookExportButton}
          />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SSE event → merged-timeline reducer (UnifiedAgent's, plus one capture: the
// consolidation result names the sheets that fed each statement).
// ---------------------------------------------------------------------------

function handleDatabookEvent(event, role, setChatItems, setMappings, mergeSources) {
  const { type, data } = event;

  if (type === 'text_delta') {
    const chunk = data?.text || '';
    if (!chunk) return;
    setChatItems((prev) => {
      const last = prev[prev.length - 1];
      if (last?.type === 'assistant' && last.isStreaming) {
        return [...prev.slice(0, -1), { ...last, text: last.text + chunk }];
      }
      return [
        ...prev,
        { id: newId(), type: 'assistant', text: chunk, isStreaming: true, role },
      ];
    });
    return;
  }

  if (type === 'tool_start') {
    setChatItems((prev) => {
      const closed = prev.map((it) =>
        it.type === 'assistant' && it.isStreaming
          ? { ...it, isStreaming: false }
          : it,
      );
      const last = closed[closed.length - 1];
      const entry = {
        toolUseId: data.tool_use_id,
        toolName: data.tool_name,
        input: data.input,
        status: 'running',
      };
      if (last?.type === 'tool_group' && last.isRunning) {
        return [...closed.slice(0, -1), { ...last, calls: [...last.calls, entry] }];
      }
      return [
        ...closed,
        { id: newId(), type: 'tool_group', calls: [entry], isRunning: true, role },
      ];
    });
    return;
  }

  if (type === 'tool_result') {
    if (data.tool_name === CONSOLIDATE_TOOL && !data.is_error) {
      mergeSources(sourcesFromConsolidation(data.result));
    }
    setChatItems((prev) =>
      prev.map((it) => {
        if (it.type !== 'tool_group') return it;
        let touched = false;
        const calls = it.calls.map((c) => {
          if (c.toolUseId !== data.tool_use_id) return c;
          touched = true;
          return {
            ...c,
            result: data.result,
            isError: !!data.is_error,
            status: data.is_error ? 'error' : 'done',
          };
        });
        if (!touched) return it;
        const stillRunning = calls.some((c) => c.status === 'running');
        return { ...it, calls, isRunning: stillRunning };
      }),
    );
    return;
  }

  if (type === 'mapping_complete') {
    setMappings((prev) => ({ ...prev, [role]: data }));
    setChatItems((prev) => {
      const closed = prev.map((it) =>
        it.type === 'assistant' && it.isStreaming
          ? { ...it, isStreaming: false }
          : it.type === 'tool_group' && it.isRunning
            ? { ...it, isRunning: false }
            : it,
      );
      return [...closed, { id: newId(), type: 'phase_done', role }];
    });
    return;
  }

  if (type === 'done') {
    setChatItems((prev) =>
      prev.map((it) =>
        it.type === 'assistant' && it.isStreaming
          ? { ...it, isStreaming: false }
          : it.type === 'tool_group' && it.isRunning
            ? { ...it, isRunning: false }
            : it,
      ),
    );
    return;
  }

  if (type === 'error') {
    setChatItems((prev) => [
      ...prev,
      {
        id: newId(),
        type: 'assistant',
        text: `⚠ ${data?.message || 'error'}`,
        isStreaming: false,
        role,
      },
    ]);
  }
}

export default DatabookAgent;
